import React, { useEffect, useState } from "react";
import { Smartphone, X, Download } from "lucide-react";

const DISMISS_KEY = "ma_pwa_install_dismissed";
const DISMISS_UNTIL = 14 * 24 * 60 * 60 * 1000; // 14 days

/**
 * PWA install prompt — shows an elegant dark/gold banner when the browser fires
 * `beforeinstallprompt`. Auto-detects iOS Safari (no prompt event) and displays
 * a mini tutorial instead.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosVisible, setIosVisible] = useState(false);

  useEffect(() => {
    // Skip if already installed / running as PWA
    if (window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone) return;

    // Skip if dismissed recently
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw && Date.now() - parseInt(raw, 10) < DISMISS_UNTIL) return;
    } catch { /* ignore */ }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari — no beforeinstallprompt, show a hint after a delay
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) {
      const t = setTimeout(() => setIosVisible(true), 8000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    if (choice.outcome !== "accepted") {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    }
  };

  const dismiss = () => {
    setVisible(false);
    setIosVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (!visible && !iosVisible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[80]"
    >
      <div className="bg-[#0a0a0a] border border-[#DCB457]/40 shadow-2xl overflow-hidden animate-in slide-in-from-bottom">
        <div className="flex items-start gap-3 p-4 md:p-5">
          <div className="w-11 h-11 flex items-center justify-center bg-[#DCB457]/10 border border-[#DCB457]/30 rounded-sm shrink-0">
            <Smartphone size={18} strokeWidth={1.4} className="text-[#DCB457]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457] mb-1">
              {iosVisible ? "Installer sur iPhone" : "Installer l'app"}
            </p>
            <h3 className="font-serif-luxe text-lg leading-tight mb-1">
              My Animation en un tap
            </h3>
            <p className="text-xs text-white/60 leading-relaxed">
              {iosVisible ? (
                <>Appuyez sur <b className="text-white/85">Partager</b> puis
                {" "}<b className="text-white/85">« Sur l'écran d'accueil »</b>.</>
              ) : (
                "Ajoutez l'app à votre écran d'accueil — accès direct à votre décompte, RDV, playlist."
              )}
            </p>
            {!iosVisible && (
              <button
                onClick={install}
                data-testid="pwa-install-btn"
                className="btn-gold mt-3 !text-[11px] !py-2 !px-4 inline-flex items-center gap-1.5"
              >
                <Download size={12} strokeWidth={1.5} /> Installer
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            data-testid="pwa-dismiss-btn"
            aria-label="Fermer"
            className="text-white/40 hover:text-[#DCB457] shrink-0"
          >
            <X size={16} strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </div>
  );
}
